import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  IconLayoutDashboard, IconBoxSeam, IconUsers, IconSettings, IconReceipt2,
  IconUserCog, IconCash, IconChevronLeft, IconLogout, IconShoppingCart,
  IconMenu2, IconX, IconPackages, IconShieldHalf, IconBuildingStore,
  IconActivity, IconQrcode, IconPhoto,
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { IconSun, IconMoon } from '@tabler/icons-react';
import CodedPill from '../CodedPill';

/**
 * AdminLayout — Phase 4.1.
 *
 * Two-column shell for /admin/* routes:
 *   - LEFT: collapsible sidebar with section nav. Active item is
 *     highlighted. On mobile/tablet, the sidebar drawer is overlayed
 *     and dismissed by tapping the backdrop.
 *   - RIGHT: top header + page content. Header shows the breadcrumb-
 *     like page title, theme toggle, "Back to POS" link, and the
 *     signed-in user.
 *
 * Role gate is enforced at the route level (RequireMinRoleRoute) —
 * by the time this layout renders, we already know the user is
 * MANAGER+. Sub-routes can further gate by minRole.
 *
 * Cohesion: every admin sub-page wraps with <AdminLayout title="...">
 * — no admin route renders raw HTML at the page level.
 */

interface NavItem {
  label: string;
  to: string;
  Icon: typeof IconLayoutDashboard;
  minRole?: 'MANAGER' | 'ADMIN' | 'OWNER';
}

const NAV: NavItem[] = [
  { label: 'Dashboard',    to: '/admin',          Icon: IconLayoutDashboard },
  { label: 'Live ops',     to: '/admin/live',     Icon: IconActivity },
  { label: 'Orders',       to: '/admin/orders',   Icon: IconReceipt2 },
  { label: 'Products',     to: '/admin/products', Icon: IconBoxSeam },
  { label: 'Stock',        to: '/admin/stock',    Icon: IconPackages },
  { label: 'Customers',    to: '/admin/customers',Icon: IconUsers },
  { label: 'Tables',       to: '/admin/tables',   Icon: IconQrcode },
  { label: 'Banners',      to: '/admin/banners',  Icon: IconPhoto },
  { label: 'Accounts',     to: '/admin/accounts', Icon: IconBuildingStore },
  { label: 'Day sessions', to: '/admin/days',     Icon: IconCash },
  { label: 'Staff',        to: '/admin/users',    Icon: IconUserCog,  minRole: 'ADMIN' },
  { label: 'Settings',     to: '/admin/settings', Icon: IconSettings, minRole: 'ADMIN' },
  { label: 'Audit log',    to: '/admin/audit',    Icon: IconShieldHalf, minRole: 'ADMIN' },
];

interface Props {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}

const ROLE_RANK: Record<string, number> = {
  CASHIER: 1, SUPERVISOR: 2, MANAGER: 3, ADMIN: 4, OWNER: 5,
};

export default function AdminLayout({ title, subtitle, actions, children }: Props) {
  const { auth, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const meetsMin = (min?: NavItem['minRole']) =>
    !min || (ROLE_RANK[auth?.role ?? ''] ?? 0) >= (ROLE_RANK[min] ?? 0);

  const initials = (auth?.name ?? 'A').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <div className="h-screen bg-light flex overflow-hidden">
      {/* Sidebar — fixed full-height column on lg+, drawer on smaller.
          Uses flex-col + mt-auto for the footer so it doesn't escape the
          aside and bleed hover state across the whole page. */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-40 w-64 bg-surface border-r border-border flex-shrink-0 transform transition-transform lg:transform-none lg:static lg:translate-x-0',
          'flex flex-col',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border flex-shrink-0">
          <img src="/icons/icon-96.png" alt="CODED" className="w-9 h-9 rounded-lg shadow-sm" />
          <div className="flex-1 leading-none">
            <p className="text-14 font-bold text-heading">CODED. Admin</p>
            <p className="text-12 font-semibold text-gray-700 mt-0.5">{auth?.branchName ?? 'HL Coffee Shop'}</p>
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-light"
            aria-label="Close menu"
          >
            <IconX size={18} stroke={2.25} />
          </button>
        </div>

        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {NAV.filter((item) => meetsMin(item.minRole)).map((item) => {
            const active = item.to === '/admin'
              ? location.pathname === '/admin'
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setDrawerOpen(false)}
                className={[
                  'flex items-center gap-3 h-11 px-3 rounded-md text-14 font-semibold transition-colors',
                  active
                    ? 'bg-primary text-[#FFFFFF] shadow-sm'
                    : 'text-heading hover:bg-light',
                ].join(' ')}
              >
                <item.Icon size={18} stroke={2} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-border space-y-1 flex-shrink-0">
          <Link
            to="/pos"
            className="flex items-center gap-3 h-10 px-3 rounded-md text-13 font-semibold text-heading hover:bg-light"
          >
            <IconShoppingCart size={16} stroke={2} />
            Back to POS
          </Link>
          <button
            type="button"
            onClick={async () => { await signOut(); navigate('/login'); }}
            className="w-full flex items-center gap-3 h-10 px-3 rounded-md text-13 font-semibold text-danger hover:bg-danger-soft"
          >
            <IconLogout size={16} stroke={2} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Drawer backdrop */}
      {drawerOpen && (
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-surface border-b border-border px-5 flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden w-9 h-9 inline-flex items-center justify-center rounded-md text-heading hover:bg-light"
            aria-label="Open menu"
          >
            <IconMenu2 size={18} stroke={2.25} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-18 font-bold text-heading leading-tight truncate">{title}</h1>
            {subtitle && <p className="text-13 font-medium text-gray-700 mt-0.5 leading-tight truncate">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <CodedPill size="sm" />
            <button
              onClick={toggleTheme}
              className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-light hover:text-primary transition-colors"
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? <IconSun size={18} stroke={1.75} /> : <IconMoon size={18} stroke={1.75} />}
            </button>
            <div className="w-9 h-9 rounded-full bg-primary text-[#FFFFFF] inline-flex items-center justify-center text-13 font-bold shadow-sm" title={`${auth?.name ?? ''} · ${auth?.role ?? ''}`}>
              {initials}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-5">
          {children}
        </main>
      </div>
    </div>
  );
}

/** Small back-link, used on second-level admin pages. */
export function AdminBackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 text-13 font-semibold text-primary hover:underline mb-3"
    >
      <IconChevronLeft size={14} stroke={2.5} />
      {label}
    </Link>
  );
}
